/** @jsx jsx */
import { css, Global, jsx } from '@emotion/core'
import 'antd-mobile/dist/antd-mobile.css' /* antd-mobile */
import Loadable from 'react-loadable'
import { HashRouter, Route, Router, Switch } from 'react-router-dom'
import { createHashHistory } from 'history'
import { syncHistoryWithStore } from 'mobx-react-router'
import { message } from 'antd'

import * as store from '@store/index'
import PageLoading from '@components/PageLoading'
import Error from '@components/Error'
import { Theme } from '@themes/theme'
import { ThemeProvider } from 'emotion-theming'
import DarkTheme from '@themes/dark.theme'
import {
  FloatingBottom,
  HideAni,
  ModalHeightAni,
  ModalOpacityValue,
  mq,
  mqH,
  mqMin,
  mqMinPCPopup,
  PCMaxWidth,
  PCModalMaxWidth,
  SafeAreaConstantPadding,
  SafeAreaEnvPadding,
  ShowPopoverAni,
} from '@styles/base.style'

import PrimaryLayout from '@views/PrimaryLayout'
// import { useOnMount } from '@utils/reactExt'
import { MESSAGE_SECOND_DURATION } from '@constants/index'
import IntlWrapper from './IntlWrapper'
import Provider from './Provider'

const hashHistory = createHashHistory()
const history = syncHistoryWithStore(hashHistory, store.routerStore)

const Login = Loadable({
  loader: () => import(/* webpackChunkName: "login" */ '@views/Login'),
  loading: PageLoading,
})

const AuthKeyLogin = Loadable({
  loader: () => import(/* webpackChunkName: "login" */ '@views/Login/AuthKeyLogin'),
  loading: PageLoading,
})

const VocLoader = Loadable({
  loader: () => import(/* webpackChunkName: "voc" */ '@views/Voc/index'),
  loading: PageLoading,
})

const GlobalStyle = (theme: Theme) => css`
  .adv-disable-scroll {
    overflow: hidden !important;
  }
  .adv-hidden {
    display: none !important;
  }
  .adv-hidden-fill-space {
    visibility: hidden !important;
  }
  .z-index-0 {
    z-index: 0 !important;
  }
  .no-scrollbar {
    &::-webkit-scrollbar {
      display: none;
    }
  }
  * {
    user-select: none;
    font-family: 'Roboto', 'Noto Sans KR', 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', '돋움', dotum, sans-serif !important;
    font-weight: ${theme.fontW.bold};
    -webkit-text-size-adjust: none;
  }
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${theme.fontW.bold};
  }
  /* custom scrollbar (크롬/사파리에서만 적용됨) */
  /* width */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: #6f777e40;
    border-radius:  10px;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background:${theme.color.disableTxt};
    border-radius: 10px;
    transition: all .3s;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #979ea5;
  }

  html {
    font-size: 10px;
  }
  body {
    vertical-align: center;
    line-height: 1.2rem;
    font-size: 1rem;
    background-color: ${theme.color.mainBg};
    text-rendering: optimizeLegibility;
    font-kerning: none;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-y: auto !important; /* 팝업이 뜨면 antd에서 강제로 overflow를 hidden하는데 S8에서 modal창의 scroll이 안되는 버그가 발생함 */
    /* *********** 절대 지우지 말기(매치업 속도 관련) *********** */
    touch-action: pan-y pinch-zoom;
    cursor: pointer;  /* pc일때는 적용되지 않아야함.. */
    /* pc */
    &.pc {
      /* max-width: ${PCMaxWidth}; */ /* body에 최대 너비 지정을 하면 edge에서 스플래쉬 화면에 스크롤 생김 */
      min-height: 600px;
      margin: 0 auto;
      display: block;
      cursor: auto;
      &:not(.event-page-body) {
        overflow-y: hidden !important;
        ${mqMin[8]} {
          overflow: hidden !important;
        }
      }
    }
    // 팝업이 열릴때 백스크롤 방지
    &.body-scroll-hidden {
      overflow: hidden !important;
      #app > * {
        /*overflow: hidden !important;*/
      }
      .am-modal-wrap.am-modal-wrap-popup, .am-modal-wrap.am-modal-wrap-popup > * ,.am-modal.full-modal.message.am-modal-popup.am-modal-popup-slide-down {
        /* -webkit-transform: translateZ(0) !important; */
      }
      .full-popup-content{
        overflow: hidden;
      }
      .content-wrap {
        height: 100vh;
        overflow: auto;
        &.safari {
          padding-bottom: 70px;
        }
      }
    }
    &.body-x-scrollable {
      overflow-x: auto !important;
    }
    &.ios {
      input[type="text"], input[type="password"], textarea {
        font-size: 16px !important;
      }
      .pick-comment-textarea {
        width: 110%;
        max-width: 110%;
        min-height: 110px !important;
        transform: scale(0.88);
        transform-origin: left top;
        margin-bottom: -10px;
        &.edit {
          margin-bottom: -13px;
        }
      }
    }
  }
  html,
  body {
    z-index: -1;
    width: 100%;
    height: 100%;
    box-sizing: border-box !important;
  }
  .point-color {
    color : ${theme.color.yellow};
    &--blue {
      color : ${theme.color.secondaryBg};
      &--light {
        color : ${theme.color.blue};
      }
      &--dark {
        color : ${theme.color.tabBg};
      }
    }
    &--red {
      color : ${theme.color.red};
      &--modal {
        color : ${theme.color.modalred};
      }
    }
    &--pink {
      color : ${theme.color.pink};
    }
    &--yellow {
      color : ${theme.color.yellow};
      &--modal {
        color : ${theme.color.modalyellow};
      }
    }
    &--orange {
      &--modal {
        color : ${theme.color.modalOrange};
      }
    }
    &--green {
      color : ${theme.color.primaryBg};
    }
    &--gray {
      &--modal {
        color : ${theme.color.modalgray};
      }
    }
  }
  .ant-layout,
  .ant-layout-content {
    background: ${theme.color.mainBg} !important;
  }
  #app {
    display: flex;
    position: relative;
    width: 100%;
    height: 100%;
    flex-direction: column;
  }
  .touch-action-disabled {
    touch-action: none !important;
  }
  .touch-action-enabled {
    touch-action: auto;
  }
  .app-scroll-hidden {
    overflow: hidden;
  }
  .m-layout {
    padding-top: 40px;
  }
  .am-modal-wrap {
    overflow-y: hidden;
    /* content 내 스크롤 없애기 */
    * {
      -ms-overflow-style: none !important;
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  div.ant-back-top {
    right: initial;
    left: 19px;
    bottom: ${FloatingBottom};
    padding-bottom: ${SafeAreaEnvPadding};
    padding-bottom: ${SafeAreaConstantPadding};
    width: 45px;
    height: auto;
    min-height: 45px;
  }

  /* 서랍 메뉴 클릭시 아이콘 및 메뉴명이 뜨는 문제 때문에 임시로 처리 */
   //일반 중앙 알림 모달
  .ant-drawer,
  .ant-modal-mask,
  .ant-modal-wrap,
  .am-modal-mask,
  .am-modal-wrap,
  .ant-popover,
  .ant-dropdown,
  .ant-select-dropdown {
    position: fixed;
    z-index: 10000;
  }
  /* z-index 순위 */
  /* 1. 최상단 zIndex */
  .am-modal-wrap-z-index-top {
    z-index: 99999;
  }
  /* 2. 긴급 메시지 팝업 */
  .urgency-message-popup {
    z-index: 10012;
  }
  /* 3. 추가 메뉴 팝업 */
  .ant-popover {
    z-index: 10007;
  }
  /* 4. 최초 진입시 모달(최상단) / 출석 체크 이벤트 모달 / 출석 체크 이벤트 보상 완료 모달 */
  .am-modal-wrap.notice-popup, .am-modal-wrap.attendance-event, .am-modal-wrap.attendance-event-complete {
    z-index: 10006;
  }
  /* 5. 가이드 알림 모달 */
  .am-modal-wrap.guide-notice-popup {
    z-index: 10005;
  }
  /* 6. 미션 모달 / 로그인 팝업, 토스트 메시지 */
  .am-modal-wrap.mission-popup, .sider-modal, .ant-message {
    z-index: 10004;
  }
  /* 7. voc 팝업 */
  .voc-popup {
    z-index: 10003;
    .modal-bg {
      opacity: .8; /* 베팅 완료 모달과 겹칠 경우 가독성이 떨어지는 현상 해결 */
    }
  }
  /* '베팅중입니다' 모달(상세 베팅 모달보다 앞에 떠야함) */
  .betting-dialog-wait {
    z-index: 10002; /* 상세 베팅 슬립보다 아래 뜨는 현상 해결 */
  }
  /* 9. 상세 베팅 모달(추가BET에서 해당 모달 뒤에 뜨는 현상 해결), 재화 변경시 초기화, 베팅권 선택 모달 / 튜토리얼 */
  .ant-drawer.bet-slip, .reset-amount, .betting-coupon-wrap, .tutorial, .z-index-10001 {
    z-index: 10001;
  }
  /* 10. 첫 가이드 알림 모달 - 가이드 팝업 중 처음으로 뜨는 팝업 (메시지함 팝업보다 뒤에 있어야함 - 출석체크로 인하여) */
  .am-modal-wrap.guide-notice-popup.first-guide-popup {
    z-index: 9999;
  }
  // 메시지 플로팅 버튼 / componentBetSlip(dockZindex)
  .message-wrapper {
    z-index: 102;
  }
  // 채팅(열림)
  #chatWrap.open-chat-wrap {
    z-index: 101;
  }
  /* Nav / 프리미엄 분석가 홍보 연출 / 오늘의승부사(매치업 배너) */
  .navbar, .pick-promotion-btn, .today-user-pick-matchup-banner {
    z-index: 100;
  }
  /* 클럽쪽지 알림 / 채팅 */
  .club-message-alert, #chatWrap {
    z-index: 99;
  }
  .ant-back-top {
    z-index: 10;
  }

  /* .ant-popover.ant-popover-placement-top */

  input,
  label,
  .ant-input.contents,textarea {
    user-select: auto;
  }
  input::placeholder,
  textarea::placeholder {
    color: ${theme.color.modalSubTxt};
  }
  .ant-input {
    background: ${theme.color.TP} !important;
    transition: all 1s;
    /* 로그인 화면 아이디, 비밀번호 입력란 */
    &#username,
    &#password {
      background: ${theme.color.TP} !important;
    }
  }
  .login-popup, .ant-drawer.sider {
    .close-btn {
      &::before, &::after {
        background: ${theme.color.modalgray};
      }
    }
  }
  /* 서랍메뉴 */
  .sider-modal.am-modal-wrap {
    .sider.am-modal.full-modal {
      .am-modal-content {
        .am-modal-body {
          height: 100%;
        }
      }
    }
  }
  .ant-input:hover,
  .ant-input:focus,
  .ant-input:active {
    border-bottom: 1px solid ${theme.color.TP} !important;
    box-shadow: none !important;
    /* box-shadow: 0 0 0 2px ${theme.color.primaryBg} !important; */
    &#username, &#password {
      border-bottom: none !important;
    }
  }
  /* text 입력 란에만 밑선 적용 */
  .text-input {
    .ant-input:hover,
    .ant-input:focus,
    .ant-input:active,
    &.ant-input:hover,
    &.ant-input:focus,
    &.ant-input:active  {
      border-bottom: 1px solid ${theme.color.primaryBg} !important;
    }
  }
  h1,
  h2,
  h3,
  h4,
  h5 {
    color: ${theme.color.themeTxt};
    margin: 0;
  }
  p,
  span,
  div,
  a {
    margin-bottom: 0;
    font-size: ${theme.font.medium};
    color: ${theme.color.themeTxt};
  }
  a {
    &:hover {
      color: inherit;
    }
  }
  p {
    display: inline-block;
  }
  ul,
  ol,
  li {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  svg,
  svg path {
    fill: currentcolor;
  }
  a, button, input, label, .am-accordion .am-accordion-item .am-accordion-header {
    cursor: pointer;
  }
  .disabled, .ant-radio-button-wrapper-disabled, .ant-input-disabled, .ant-input-disabled + .ant-input-group-addon > .ant-btn, .ant-collapse-item-disabled > .ant-collapse-header {
    cursor: default !important;
  }
  .cursor-pointer {
    cursor: pointer !important;
  }
  /* carousel active색 설정 */
  .am-carousel-wrap-dot-active > span {
    position: relative;
    background: ${theme.color.primary};
  }

  /* 서랍 메뉴 */
  .center-table {
    table td {
      text-align: center;
    }
    .ant-table-thead > tr > th {
      text-align: center;
    }
  }
  .ant-menu-item:active,
  .ant-menu-submenu-title:active {
    background: none !important;
  }

  /* antd checkbox */
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${theme.color.primary} !important;
    border-color: ${theme.color.primary} !important;
  }

  /* antd radio button */
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):focus-within, .ant-radio-button-wrapper:focus-within {
    outline: none  !important;
  }

  /* drawer */
  .ant-drawer-content {
    .ant-drawer-wrapper-body {
      background: ${theme.color.contentsBg};
      .ant-drawer-header {
        padding: 15.5px 24px;
        background: ${theme.color.contentsBg};
        text-align: center;
        border-bottom: ${theme.border.line};
        .ant-drawer-title {
          color: ${theme.color.themeTxt};
        }
        .ant-drawer-close {
          color: ${theme.color.themeTxt};
        }
      }
      /* 서랍 메뉴 배경 */
      .ant-drawer-body {
        padding: 0;
        background: ${theme.color.modalBg} !important;
      }
    }
  }
  .club-info {
    .ant-drawer-content .ant-drawer-wrapper-body .ant-drawer-header {
      border-bottom: none;
    }
  }
  /* popup contents scroll되도록 */
  .ant-drawer-wrapper-body {
    height: calc(100vh - 0.01px);
    overflow-y: scroll;
  }
  /* popoup 상단 선 제거 */
  .ant-drawer-header {
    /* border-bottom: none !important; */
  }
  .loginPopup.ant-drawer-bottom.ant-drawer-open .ant-drawer-content-wrapper,
  .loginPopup.ant-drawer-bottom .ant-drawer-content,
  .Message.ant-drawer-bottom.ant-drawer-open .ant-drawer-content-wrapper,
  .Message.ant-drawer-bottom .ant-drawer-content,
  .PickSale.ant-drawer-bottom.ant-drawer-open .ant-drawer-content-wrapper,
  .PickSale.ant-drawer-bottom .ant-drawer-content {
    border-radius: 10px 10px 0 0;
  }
  /* round Drawer */
  .Message {
    .ant-drawer-content {
      .ant-drawer-wrapper-body {
        border-radius: 10px 10px 0 0;
        .ant-drawer-body {
          border-radius: 10px 10px 0 0;
        }
      }
    }
  }
  /* login error */
  .ant-form-item {
    .ant-form-explain {
      display: none !important;
      font-size: ${theme.font.small};
      position: absolute;
      top: 105%;
      width:100%;
      padding-left: 5px;
      text-align:left;
    }
    &.ant-form-item-with-help {
      .ant-form-explain {
        display: block !important;
      }
    }
  }

  /* collapse */
  .ant-collapse > .ant-collapse-item > .ant-collapse-header,
  .ant-collapse-content > .ant-collapse-content-box {
    background: ${theme.color.contentsBg};
  }
  .ant-collapse,
  .ant-collapse-item:last-of-type > .ant-collapse-content,
  .ant-collapse > .ant-collapse-item:last-of-type,
  .ant-collapse > .ant-collapse-item:last-of-type > .ant-collapse-header {
    border-radius: 0;
  }
  .ant-collapse,
  .ant-collapse-content,
  .ant-collapse > .ant-collapse-item {
    border: none !important;
  }

  /* accordion */
  /* pc버전에서 나오는 라인 제거 */
  .am-list-body, .am-accordion .am-accordion-item .am-accordion-header, .am-accordion .am-accordion-item .am-accordion-content .am-accordion-content-box, .am-tabs-default-bar-top .am-tabs-default-bar-tab {
    border-top: none;
    border-bottom: none;
  }
  .am-accordion {
    border: none;
  }
  .am-accordion::before,
  .am-accordion .am-accordion-item .am-accordion-header::after {
    background-color: ${theme.color.disableTxt} !important;
  }
  .am-accordion .am-accordion-item .am-accordion-header {
    position: relative;
    background: ${theme.color.contentsBg};
  }
  .am-accordion .am-accordion-item .am-accordion-content {
    background: ${theme.color.mainBg};
  }

  .am-accordion .am-accordion-item .am-accordion-content .am-accordion-content-box::after,
  .am-accordion::before,
  .am-accordion .am-accordion-item .am-accordion-header::after {
    display: none !important;
  }

  /* list */
  .ant-list.ant-list-split {
    .ant-list-empty-text {
      padding: 0;
    }
  }
  /* antd-mobile */
  .am-list .am-list-item {
    padding-left: 0;
    .am-list-line {
      padding: 0 20px;
    }
  }

  /* select */
  .ant-select-open .ant-select-selection,
  .ant-select-focucsed .ant-select-selection,
  .ant-select-selection:focus,
  .ant-select-selection:active,
  .ant-select-selection:hover {
    box-shadow: none;
    border-color: ${theme.color.primary};
    background: ${theme.color.contentsBg};
  }
  .ant-select-dropdown {
    background: ${theme.color.TP} !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;
    .ant-select-dropdown-menu {
      border: none !important;
      background: none !important;
      .ant-select-dropdown-menu-item {
        height: 33px !important;
        padding: 0 15px !important;
        background: ${theme.color.contentsBg};
        border: none !important;
        font-size: ${theme.font.medium};
        .option-txt {
          line-height: 3.3rem;
          font-size: ${theme.font.small};
        }
        &:hover {
          background: ${theme.color.themeTxt} !important;
          span {
            color: ${theme.color.modalTxt};
          }
        }
        &.ant-select-dropdown-menu-item-selected {
          background: ${theme.color.primary} !important;
        }
      }
    }

    &.dark-dropdown {
      .ant-select-dropdown-menu-item {
        line-height: 33px;
        color: ${theme.color.themeTxt};
        &.ant-select-dropdown-menu-item-selected, &.ant-select-dropdown-menu-item-active {
          color: ${theme.color.mainBg};
        }
      }
    }
  }

  /* **** Modal **** */
  /* antd-mobile */
  .am-modal-mask {
    display: none;
    background-color: rgba(0, 0, 0, 0.8);
  }
  .am-modal:not(.adv-new-dialog) {
    .am-modal-content {
      .am-modal-header {
        padding-bottom: 14px;
        .am-modal-title {
          font-size: ${theme.font.large};
        }
      }
    }
  }
  .am-modal-wrap {
    &:not(.chat, .full-modal, .event, .filter-page-popup) {
      .am-modal-content{
        .am-modal-header {
          padding-bottom: 15px;
        }
        ${mqH[0]} {
          overflow: visible;
        }
      }
    }
  }

  /* 풀팝업 */
  .full-modal.am-modal-popup {
    height: 100%;
    .am-modal-content {
      height: 100%;
      padding: 0 ;
      border-radius:0;
      background: ${theme.color.mainBg} !important;
      overflow: hidden; /* 모달창 전체가 스크롤 되는 현상 제어 */
      .am-modal-header {
        position: relative;
        height: 45px;
        padding: 0;
        .am-modal-title {
          line-height: 4.5rem;
          .title {
            font-size: ${theme.font.large};
            &.page-tit {
              font-size: ${theme.font.title};
            }
          }
        }
      }
      .am-modal-body {
        height: calc(100% - 45px);
        // 메세지함에서 받은 베팅 내역
        .title-wrap.modal.betting-history {
          display: none;
        }
        .betting-history-wrap {
          &.pc {
            .am-list::-webkit-scrollbar {
              display: block !important; /* 팝업내 스크롤바 보이도록 */
            }
          }
        }
        .chat-tab-wrap .am-tabs-pane-wrap::-webkit-scrollbar {
          display: block !important; /* 팝업내 스크롤바 보이도록 */
        }
      }
    }
    /* 메시지함 */
    &.message {
      .am-modal-content .am-modal-body {
        overflow: hidden;  /* tab영역이 스크롤 되는것 방지 */
      }
    }
    /* title이 흐려지는 것 방지 */
    &.title-clear {
      .am-modal-content .am-modal-body {
        overflow: hidden; 
      }
    }
    /* 이미지 단독 출력 풀팝업 (주요 유저 관리 마이페이지-상세이미지 팝업) */
    &.full-image-popup {
      .am-modal-body {
        .full-modal {
          width: 100% !important;
          > img {
            width: 100%;
            height: auto;
          }
        }
      }
    }
    /* 풀팝업 닫기 버튼 위치 조정 스타일 */
    &.cls-btn-bottom {
      .full-modal-cls-btn {
        transform: translateY(60px);
      }
    }
  }

  // match filter popup
  .filter-page-popup {
    .full-modal.am-modal-popup {
      .am-modal-content {
        .am-modal-header {
          height: auto;
          padding-bottom: 0;
        }
        .am-modal-body .match-list.pc .match-block-body .am-accordion-content {
            max-width: 519px;
        }
        .am-modal-body {
          height: 100%;
          overflow: hidden;
        }
      }
    }
  }

  /* 배당률 변경 모달창 */
  .change-rate .am-modal-content {
    padding: 12px 0 0 !important;
    .am-modal-header {
      padding: 10px 0;
      .am-modal-title {
        font-size: ${theme.font.large};
      }
    }
  }
  .am-modal-transparent:not(.adv-new-dialog, .event) {
    width: 69vw !important;
    .am-modal-content {
      padding-top: 20px;
      border-radius: 16px;
      background: ${theme.color.modalBg};
      .am-modal-body {
        padding: 0 16px 0;
        /* Dialog를 이용해 적용된 text */
        & > p:last-of-type {
          padding-bottom: 16px;
        }
        & > p + .custom-dialog-btn-wrap {
          padding-top: 0;
        }
      }
    }
  }
  .am-modal-popup .am-modal-content {
    padding: 30px 20px 20px;
    border-radius: 16px 16px 0 0;
    .am-modal-footer .am-modal-button-group-v .am-modal-button::before {
      display: none;
    }
  }
  /* custom-dialog: 큰 사이즈 modal */
  /* 매치 정보(Visualization Popup) 팝업 / 중앙에 뜨는 팝업 / matchup filter popup */
  .custom-dialog.am-modal-transparent {
    /* width: 90% !important; */
    width: 90vw !important;
    .am-modal-content {
      padding-top: 0;
      background-color: ${theme.color.modalBg};
      overflow: visible !important;
      /* padding-top: 10px; */
      .am-modal-header {
        position: relative;
        z-index:2;
        padding: 20px 0 10px;
        background-color: ${theme.color.modalBg};
        border-radius: 16px 16px 0 0;
        .am-modal-title {
          font-size: ${theme.font.large};
          color: ${theme.color.modalTxt};
          line-height: 2rem;
          // 모달 title에 아이콘이 들어가는 경우
          .modal-title-league-ico {
            width: 100%;
            max-width: 20px;
            height: auto;
            max-height: 20px;
            margin: -2px 6px 0 0;
          }
          // 경고 모달 아이콘
          .modal-warning-ico {
            width: 20px;
            height: auto;
            margin-right: 4px;
            margin-top: -2px;
          }
          .title {
            font-size: ${theme.font.large};
            color: ${theme.color.modalTxt};
            line-height: 1.2rem;
          }
          .notice {
            font-size: ${theme.font.small};
            color: ${theme.color.modalred};
            line-height: 1rem;
          }
        }
      }
      .am-modal-body {
        /* padding: 0 0 16px 0; */
        padding: 0;
        padding-bottom: 0;
        overflow: visible !important;
        .ant-row {
          padding: 0 20px;
        }
      }
    }
    /* matchup filter popup */
    &.filter-popup.am-modal.am-modal-transparent {
      .am-modal-content {
        background-color: ${theme.color.modalBg};
        padding: 0;
        .am-modal-body {
          padding: 0;
          padding-bottom: 0;
        }
      }
      button.ant-modal-close {
        display: none;
      }
    }
    /* custom-dialog: 중간 사이즈 modal */
    /* 매치업 핸디캡/언더오버 기준점 modal */
    &.middle-size {
      width: calc(100% - 25%) !important;
    }
    /* 캐릭터가 있는 팝업 */
    &.character-popup {
      top: 10vh;
    }
  }
  /* normal-dialog: 작은 사이즈 modal */
  .normal-dialog.am-modal {
    .am-modal-content {
      padding-top: 30px;
      padding-bottom:0;
      background-color: ${theme.color.modalBg};
      .am-modal-title {
        font-size: ${theme.font.large};
        color: ${theme.color.modalTxt};
      }
      .am-modal-body {
        overflow: visible;
      }
    }
    // 좌측 정렬 팝업
    &.align-left-dialog {
      .am-modal-body {
        text-align: left;
        p {
          font-size: ${theme.font.medium};
        }
      }
    }
  }
  .short-top.am-modal-popup {
    .am-modal-content {
      padding: 16px 14px 0;
      background: ${theme.color.modalBg};
      .am-modal-title {
        color: ${theme.color.modalTxt};
      }
      .am-modal-body {
        overflow: visible;
      }
    }
    // 공유 베팅 슬립
    &.pick-bet-slip-popup {
      .am-modal-content {
        padding: 0;
      }
    }
    /* 상단 여백 좁은 팝업 */
    &.message-popup {
      height: auto;
      .am-modal-content {
        background-color: ${theme.color.modalBg};
      }
      &.white-popup {
        .am-modal-content {
          padding: 24px 14px 16px;
          background: ${theme.color.themeTxt};
        }
      }
    }
  }

  // 베팅 완료 모달
  .bet-success-modal {
    .am-modal-content {
      overflow: visible;
    }
  }
    /* 이벤트 팝업 */
  .event.am-modal.am-modal-transparent {
    width: 100% !important;
    height: 100%;
    .am-modal-content {
      padding-top: 0;
      background: transparent !important;
      border-radius: 0;
      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 42px;
        left: 0;
        width: 100%;
      }
      .am-modal-body {
        /* position: absolute;
        top:50%;
        left: 50%;
        width: auto;
        height: auto;
        transform: translate(-50%,-50%); */
      }
    }
    .modal-bg {
      z-index: 0;
    }
  }
  /* 베팅권 팝업 */
  .betting-coupon.am-modal-popup-slide-up {
    .am-modal-content {
      .am-modal-header {
        padding-bottom: 0 !important;
        line-height: 2.3rem;
        .am-modal-title {
          font-size: ${theme.font.title};
        }
      }
    }
  }
  /* 사용한 베팅권 팝업 */
  .used-betting-coupon.am-modal-popup-slide-up {
    .am-modal-content {
      .am-modal-header {
        padding-bottom: 14px !important;
        .am-modal-title {
          font-size: ${theme.font.title};
        }
      }
    }
  }
  /* 채팅 팝업 */
  .chat.am-modal {
    height: 315px;
    .am-modal-content {
      padding:0;
      background: ${theme.color.modalSubBg};
      .am-modal-header{
        padding: 4px 15px;
        background: ${theme.color.modalSubBg};
        .am-modal-title {
          display: flex;
          flex-wrap: nowrap;
          justify-content: space-between;
        }
      }
      .am-modal-body {
        height: 275px;
      }
    }
    .ant-list-items {
      position: relative;
      bottom: 0;
      width: 100%;
      height: auto;
      max-height: 217px;
      overflow-y: scroll;
    }
  }
  /* 채팅 풀 팝업 */
  .max.am-modal {
    height: 100%;
    transition: all .3s;
    animation: ${ModalHeightAni} 1s forwards;
    .am-modal-content {
      border-radius: 0;
      .am-modal-body {
        height: auto;
      }
    }
    .ant-list-items {
      max-height: calc(100vh - 102px);
    }
    .ant-list-split .ant-list-item:last-of-type {
      margin-bottom: 0;
    }
    .arrow {
      transform: translate(-50%, -50%) rotate(0deg);
    }
  }
  /* 클럽 찾기 팝업 */
  .club-portal.am-modal.am-modal-popup  {
    .am-modal-content {
      height: 100vh;
      border-radius: 0;
      .am-modal-header {
        height:54px;
        padding:0;
        background: ${theme.color.contentsBg};
        border-bottom: ${theme.border.line};
        .am-modal-title {
          position:relative;
          top: 50%;
          transform: translate(0,-50%);
        }
      }
      .am-modal-body {
        height: calc(100vh - 54px);
        overflow: scroll;
      }
    }
    .am-modal-content {
      padding: 0;
    }
  }

  /* message(토스트 메세지) */
  .ant-message {
    -webkit-transform: translateZ(1px);
    .ant-message-notice-content {
      position: relative;
      width: calc(100% - 32px);
      height: auto;
      padding: 21px;
      text-align: center;
      border-radius: 10px;
      background: ${theme.color.themeTxt};
      opacity:.95;
      pointer-events: none;
      overflow: hidden;
      .ant-message-custom-content {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        line-height: 1.5rem;
        .live-score-toast {
          display: inline-flex;
          width: calc(100% + 10px);
          transform: translateX(-15px);
        }
        &::before {
          content:'';
          display: block;
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: ${theme.color.primaryBg};
        }
        i {
          top: 0;
          transform: rotate(180deg);
          margin-right: 0;
          color: ${theme.color.primaryBg};
          font-size: ${theme.font.xxlarge};
        }
        span {
          opacity:1;
          margin-left: 14px;
          color: ${theme.color.modalTxt};
          font-size: ${theme.font.large};
        }
      }
    }
  }
  .ant-message-info .anticon,
  .ant-message-loading .anticon {
    color: ${theme.color.primary};
  }

/* popover */
  .ant-popover.zoom-big-leave {
    -webkit-transform: translateZ(1px);
    .special-popover {
      animation: ${HideAni} 0.25s ease-in-out;
    }
  }
  .ant-popover .ant-popover-inner,
  .ant-popover-placement-top > .ant-popover-content > .ant-popover-arrow,
  .ant-popover .ant-popover-content > .ant-popover-arrow {
    background: ${theme.color.themeTxt};
    border-color: ${theme.color.themeTxt} !important;
  }
  .ant-popover {
    * {
      color: ${theme.color.mainBg};
    }
    &.kakao-login { // 카카오 이관 관련 팝업
      .point-color--orange--modal {
        color: ${theme.color.modalOrange};
      }
    }      
  }

  .ant-popover.ant-popover-placement-topRight, .ant-popover.ant-popover-placement-top, .ant-popover.ant-popover-placement-bottomRight, .ant-popover.ant-popover-placement-bottomLeft {
    top: 470px;
    .ant-popover-content {
      .ant-popover-inner {
        max-height: 60vh;
        overflow-x: hidden;
        overflow-y: auto;
        box-shadow: 1px 2px 3px rgba(0,0,0, .6);
        border-radius: 6px;
        .ant-popover-title {
          min-height: 0;
          padding: 5px 10px;
          .popover-title-txt {
            svg {
              position: relative;
              top: 2px;
              left: 1px;
              width: 12px;
            }
          }
        }
        .ant-popover-inner-content {
          padding: 5px 10px;
          background: ${theme.color.modalSubBg2};
          .popover-content-txt {
            display: inline-flex;
            justify-items: flex-start;
            align-items: center;
            font-size: ${theme.font.small};
            color: ${theme.color.modalSubTxt};
            svg {
              top: 0;
              left: 0;
              width: 10px;
              transform: translateY(-3%);
            }
          }
        }
      }
      .ant-popover-arrow {
        z-index: 1;
        background: none;
        border-color: ${theme.color.modalSubBg2} !important;
        box-shadow: 3px 2px 3px rgba(0,0,0, .6);
      }
    }
    &.white-popover {
      &.mobile {
        z-index: 2 !important;
      }
      .ant-popover-content {
        .ant-popover-arrow {
          border-color: ${theme.color.themeTxt} !important;
        }
        .ant-popover-inner {
          .ant-popover-inner-content {
            background: ${theme.color.themeTxt};
            .popover-content-txt {
              font-size: ${theme.font.medium};
            }
          }
        }
      }
      &.only-txt {
        .ant-popover-inner-content {
          .popover-content-txt {
            display: inline-block;
          }
        }
      }
    }

    &.gray-popover {
      &.mobile {
        //z-index: 2 !important;
      }
      .ant-popover-arrow, .ant-popover-content .ant-popover-inner .ant-popover-inner-content {
        border-color: ${theme.color.modalSubBg} !important;
        background: ${theme.color.modalSubBg};
      }
      .ant-popover-content {
        .ant-popover-inner {
          .ant-popover-inner-content {
            //padding: 8px 12px;
            .popover-content-txt {
              color: ${theme.color.modalTxt};
              //font-size: ${theme.font.medium};
              font-weight: ${theme.fontW.medium};
            }
          }
        }
      }
    }
  }
  .ant-popover.ant-popover-placement-bottomRight, .ant-popover.ant-popover-placement-bottomLeft {
    .ant-popover-inner {
      position: relative;
      z-index: 1;
    }
  }
  // 베팅 내역 퀵 메뉴
  .ant-popover.betting-history-quick-menu {
    //z-index: 3 !important;
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-content {
      .ant-popover-inner {
        box-shadow: 0 3px 6px rgba(0,0,0,0.16);
        border-radius: 7px;
        .ant-popover-inner-content {
          width: 137px;
          height: auto;
          padding: 10px;
          background: ${theme.color.themeTxt};
          border-radius: 7px;
        }
      }
    }
    &.favorite-user-setting {
      .ant-popover-content {
        .ant-popover-inner {
          .ant-popover-inner-content {
            width: 110px;
          }
        }
      }
    }
  }
  .ant-popover.match-pick-menu {
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-content {
      .ant-popover-inner {
        box-shadow: 0 3px 6px rgba(0,0,0,0.16);
        border-radius: 7px;
        .ant-popover-inner-content {
          width: auto;
          height: auto;
          padding: 6px;
          background: ${theme.color.themeTxt};
          border-radius: 7px;
        }
      }
    }
  }
  // 채팅 상단 라이브 스코어 타임라인
  .ant-popover.live-match-timeline {
    padding: 0 10px;
    &.mobile {
      z-index: 2 !important;
    }
    &.bubble-modal {
      z-index: 10000 !important;
    }
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-content {
      .ant-popover-inner {
        box-shadow: 0 3px 6px rgba(0,0,0,0.16);
        border-radius: 6px;
        border: 1px solid ${theme.color.modalSubBg};
        .ant-popover-inner-content {
          width: auto;
          max-width: 93vw;
          height: auto;
          padding: 0 0 18px;
          //padding: 18px 0;
          background: ${theme.color.themeTxt};
          border-radius: 7px;
        }
      }
    }
  }
  // 나도승부사 댓글 설정
  .ant-popover.user-comment-menu{
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-content {
      .ant-popover-inner {
        box-shadow: 0 3px 6px rgba(0,0,0,0.16);
        .ant-popover-inner-content {
          padding: 0;
        }
      }
    }
  }
  /* 스페셜 popover */
  /* .ant-popover.ant-popover-placement-bottomLeft {
    padding-top:0;
    .ant-popover-content {
      .ant-popover-arrow {
        top: -3px;
      }
    }
  } */

  /* 알림 popover */
  /* .ant-popover.ant-popover-placement-bottomRight {
    left: 15vw !important;
    width: 80vw;
    padding-top:0;
    box-shadow: 0 0 10px rgba(0,0,0,.5);
    .ant-popover-inner,
   .ant-popover-content > .ant-popover-arrow,
  .ant-popover-content > .ant-popover-arrow {
    background: ${theme.color.themeTxt};
    border-color: ${theme.color.themeTxt} !important;
  }
    .alert-bakcground  {
      display:none;
      opacity:0;
      border: none;
      &::before {
        display:none;
      }
      &::after{
        content: '';
        display:none;
        position:absolute;
        top:0;
        left:0;
        background: rgb(0,0,0);
        border-radius:50%;
      }
    }
    &:not(.ant-popover-hidden) {
      .alert-bakcground {
        z-index:-1;
        display:block;
        position: absolute;
        top:-40px;
        left:-20vw;
        width: 100%;
        height:100vh;
        animation: ${ShowPopoverAni} .3s forwards ease-in-out;
        &::after {
          width: 100%;
          height:100%;
          border-radius:0;
        }
      }
    }
      .ant-popover-content > .ant-popover-arrow {
        top: -3px;
        left: auto;
        right: 8.8vw;
        background: ${theme.color.themeTxt};
        border-color: ${theme.color.themeTxt};
        ${mq[0]} {
          right: 11vw;
        }
        ${mqMin[1]} {
          right: 7.5vw;
        }
        ${mqMin[2]} {
          right: 4vw;
        }
        ${mqMin[3]} {
          right: 1.6vw;
        }
        ${mqMin[5]} {
          right: 0;
        }
      }
      .ant-popover-inner .ant-popover-title {
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;
        height: 38px;
        line-height: 3rem;
        background: ${theme.color.disableBg};
        border-bottom: none;
        .read-btn {
          top: 5px;
          width: 110px;
          height: 18px;
          padding:0;
          line-height: 1.8rem;
          font-size: ${theme.font.small};
        }
      }
      .ant-popover-inner-content {
        padding: 0;
      }
  } */

  /* pagination */
  .am-pagination {
    display: inline-block;
  }
  .am-pagination-wrap {
    font-size: ${theme.font.medium};
    .active {
      color: ${theme.color.primary};
    }
  }
  .ant-pagination-simple .ant-pagination-simple-pager input {
    color: ${theme.color.themeTxt} !important;
    background: ${theme.color.disableTxt} !important;
    border-radius: 0 !important;
  }

  /* Tab */
  .am-tabs {
    .am-tabs-tab-bar-wrap .am-tabs-default-bar {
      background-color: ${theme.color.TP} !important ;
    }
  }
  .am-tabs-default-bar-top .am-tabs-default-bar-tab::after {
    display: none !important;
  }
  .am-tabs-default-bar-top .am-tabs-default-bar-nextpage,
  .am-tabs-default-bar-bottom .am-tabs-default-bar-nextpage,
  .am-tabs-default-bar-top .am-tabs-default-bar-prevpage,
  .am-tabs-default-bar-bottom .am-tabs-default-bar-prevpage {
    display: none;
  }

  /* list */
  .am-list-header {
    padding: 0;
  }
  .am-list-body {
    background-color: ${theme.color.mainBg};
  }
  .am-list-body::before {
    display: none !important;
  }
  .am-list-body::after {
    background: none !important;
  }
  .am-list-footer {
    position: relative; /* 배너와 경기 없음 이미지 겹치는 현상 해결 */
    margin-top: 10px;
    padding: 0;
  }

  /* grid */
  .am-grid .am-flexbox {
    background-color: ${theme.color.contentsBg};
  }

  /* 승부사 알림 팝업 */
  #pwSubscribePopup {
    width: 100%;
    max-width: ${PCMaxWidth};
    .pw-subscription-popup-inner {
      width: 100%;
      height: 182px;
      border-radius: 16px 16px 0 0;
      border-width: 0 ;

      /* 상단 텍스트 영역 */
      .pw-subscription-popup-content {
        height: 146px;
        padding-top: 27px;
        flex-direction: column;
        align-items: center;
        .pw-subscription-popup-icon {
          flex: 1;
          width: 65px;
          height: 33px;
          margin: 0;
          margin-bottom: 5px;
          padding: 0;
          text-align: center;
          img {
            width: auto;
            height: 32px;
          }
        }
        .pw-subscription-popup-text {
          flex: 2;
          width: 180px;
          color:  #000;
          font-size: ${theme.font.title};
          line-height: 2rem;
          text-align: center;
        }
      }

      /* 하단 버튼 영역 */
      .pw-subscription-popup-controls {
        flex-direction: row-reverse;
        padding:0;
        .pw-subscribe-popup-button {
          flex:2;
          padding: 0;
          margin:0;
          color: ${theme.color.themeTxt};
          font-size: ${theme.font.medium};
          background: ${theme.color.btn};
          border-radius: 0;
          border: none;
          box-shadow:none;
          &-active {
            flex:3;
            background: ${theme.color.secondaryBg};
          }
        }
      }
    }
  }

  .betting-dialog-wait {
    .dialog-bg {
      background: transparent;
    }
  }

  .chatting-live-score-timeline {
    height: calc(100% - 98px);
    top: 98px;
  }

/* ****************************************************************** pc버전 ****************************************************************** */
  // pc z-index
  body.pc {
    .ant-popover {
      z-index: 10000 !important;
    }
  }
  .pc-main-header {
    z-index: 0;
  }
  .pc-layout {
    z-index: 1;
   .main-page {
      z-index: 1; /* sub-page보다 z-index가 높지 않으면 플로팅 메시지 버튼이 안눌림 */
    }
    .sub-page {
      z-index: 0;
    }
  }
  .pc-betslip:not(&.z-index-10001) {
    z-index: 1; /* 플로팅 메세지 버튼보다 아래로 뜨는 현상 해결 */
  }
  /* pc버전 modal 닫기 버튼 */
  .modal-circle-close-btn {
    display: none;
  }
  .pc {
    /* splash */
    .splash-bg-wrap {
      /* splash 뒷배경 */
      .splash-bg {
        &.mobile {
          display: none;
        }
        &.pc {
          display: block;
        }
      }
      .splash-contents {
        &--iventory {
          width: 5.8%;
          min-width: 70px;
        }
        /* 이용 등급 표시 */
        &--img {
          height: 13%;
          &.gambling {
            margin-right: 20px;
          }
          ${mq[4]} {
            height: 80px;
          }
        }
        &--wrap {
          position: absolute;
          top: 50%;
          left: 13%;
          width: 35%;
          min-width: 300px;
          height: auto;
          transform: translate(0,-38%);
          ${mq[4]} {
            left:50%;
            transform: translate(-50%,-25%);
          }
          /* 승부사 BI */
          &--bi {
            &.mobile {
              display: none;
            }
            &.pc {
              display: block;
            }
          }
          /* 이용 등급 안내 문구 */
          &--txt {
            position: relative;
            top: auto;
            left: auto;
            bottom: auto;
            transform: none;
            display: block;
            width: auto;
            height: auto;
            margin: 70px auto 0;
            padding-top: 0;
            line-height: 7rem;
            background: none;
            font-size: 42px;
            ${mq[8]} {
              line-height: 6.5rem;
              font-size: 34px;
            }
            ${mq[7]} {
              line-height: 4.5rem;
              font-size: 28px;
            }
            ${mq[6]} {
              line-height: 4rem;
              font-size: 24px;
            }
            ${mq[5]} {
              line-height: 3.5rem;
              font-size: 20px;
              margin: 60px auto 0;
            }
            ${mq[4]} {
              line-height: 3rem;
              font-size: 18px;
              margin: 40px auto 0;
            }
          }
        }
      }
    }
    /* header right button */
    .header-btn {
      .badge-style {
        top: 1px;
        right: 15px;
      }
      &.profile-btn {
        &::before {
          top: 5px ;
          right: 24px ;
        }
      }
    }
    /* modal button */
    .modal-bottom-btn, .modal-list {
      max-width: calc(100% + 28px);
    }
    .am-modal-wrap.am-modal-wrap-popup {
      overflow-x: hidden;
    }
    /* 메세지/쪽지 보내기 모달 */
    .short-top.message-popup {
      position: fixed !important; /* edge에서 sticky값을 가질때 위에서 아래로 애니메이션 효과를 갖기때문에 fixed로 수정 */
      bottom: 0;
    }
   /* 메세지/쪽지/픽구매/픽판매/하트보내기/아이템샵 - 아이템구매모달 */
    .pc-close-btn-modal.short-top.am-modal {
      /* position: fixed로 바뀌면서 바뀌어야할 속성들 */
      top: auto;
      bottom: 0;
      height: auto;
      .modal-circle-close-btn {
        display: block;
      }
      .am-modal-content {
        position:absolute;
        bottom: 0;
        width: 100%;
        height: auto;
        overflow: visible;
        /* 메세지/쪽지 보내기 모달 하단 버튼 */
        .one-button.bottom-btn{
          width: ${PCModalMaxWidth};
        }
      }
    }
    .slide-up.pc-close-btn-modal {
      .am-modal-content {
        overflow: visible;
      }
      .modal-circle-close-btn {
        display: block;
      }
    }
    .message-popup.short-top {
      top: calc(100vh - 388px);
    }
    .club-notice-popup.short-top {
       .club-notice-popup-content {
          max-height: 367px;
       }
    }
    .pick-purchase.am-modal-popup-slide-up {
      bottom: 40px;
    }
    .am-modal-wrap-popup, .custom-dialog.am-modal-transparent  {
      margin: 0 auto;
      .am-modal {
        max-width:  ${PCModalMaxWidth} !important;
        height: 100%;
        left: 50%;
        transform: translate(-50%,0);
      }
    }
    .short-top.am-modal-popup .am-modal-content, .slide-up.am-modal-popup .am-modal-content {
      height: auto;
      position: absolute;
      bottom: 0;
      width: 100%;
    }
    /* 리그 선택 모달 */
    .custom-dialog.am-modal-transparent {
      max-width:  ${PCModalMaxWidth};
    }
    .am-modal-transparent:not(.custom-dialog)  {
      max-width: 350px;
    }
    /* 이벤트 팝업 */
    .event.am-modal.am-modal-transparent {
      max-width: 100%;
      .gift-box-area {
        min-height: 386px;
      }
      .gift-box-img {
        height: 356px;
        &.reward {
          height: auto;
        }
      }
    }
    .pick-bet-slip-popup {
      position: fixed !important; /* edge에서 sticky값을 가질때 위에서 아래로 애니메이션 효과를 갖기때문에 fixed로 수정 */
      bottom: 0;
      .am-modal-content {
        height: 194px;
        .expected-gmoney .gmoney {
          top: 18px;
        }
      }
    }
    /* pc 로그인 팝업 */
    .login-popup {
      display: flex;
      align-items: center;
      .am-modal-content {
        width: 100%;
        height: 620px;
        border-radius: 16px;
        *:not(.ant-form-explain) {
          font-size: ${theme.font.large} !important;
        }
        .ant-form-explain {
          font-size: ${theme.font.medium};
        }
        .close-btn {
          top: 10px;
          right: 12px;
          &::before, &::after {
            width: 20px;
            background: ${theme.color.modalgray};
          }
        }
        .ant-form-item-btn {
          display: none;
        }
        .id-save {
          font-size: ${theme.font.large};
          .ant-checkbox {
            width: 20px;
            height: 20px;
            .ant-checkbox-inner {
              &::before, &::after {
                left: 32%;
                width: 7px;
                height: 11px;
              }
            }
          }
        }
        .ant-form-item .row {
          padding: 0 4px;
          #findId, #findPw, #signUp {
            font-size: ${theme.font.large};
          }
        }
        .social-login-txt {
          margin: 30px 0 25px;
          svg {
            width: 17px;
          }
          &::before, &::after {
            width: 30%;
          }
        }
      }
    }
    /* 풀팝업 */
    .full-modal.am-modal-popup {
      min-width: ${PCModalMaxWidth};
      max-width: ${PCModalMaxWidth} !important;
      //max-height:700px;
      max-height:75vh;
      top: 50%;
      left: 50%;
      transform: translate(-50%,-50%);
      &.adv-casino {
        min-width: 600px;
      }
    }
    /* 캐릭터 팝업(1폴드 배팅 가능 클럽) */
    .custom-dialog.middle-size.character-popup {
      max-width: 284px;
      .character-img-wrap {
        width: 284px;
        .character-img {
          right: 0;
        }
      }
    }
    /* message(토스트 메세지) */
    .ant-message {
      .ant-message-notice-content {
        max-width: 450px;
      }
    }
    /* 서랍메뉴 */
    .sider-modal.am-modal-wrap {
      top: 60px;
      width: 100%;
      max-width: ${PCMaxWidth};
      height: calc(100vh - 60px);
      overflow: visible;
      ${mq[7]} {
        left: 0;
      }
      ${mqMin[7]} {
        left: 50%;
        transform: translate(-50%,0);
      }
      .sider-mask {
        top: 0;
        transform: translate(-50%, -1px);
        width: 150vw;
      }
      .sider.am-modal.full-modal {
        top: 1px;
        left: 255px;
        transform: translate(0, 0);
        .am-modal-content {
          max-width: 440px;
          height: calc(100vh - 60px);
          visibility: visible;
          opacity: 1;
          transition: transform .3s, ${ModalOpacityValue} .15s;
        }
      }
    }
    /* page -> modal (아이템샵/베팅내역) */
    /* page title */
    .title-wrap.modal:not(.item-shop) {
      display: none;
    }
    /* item shop header(pc) */
    .title-wrap.modal.item-shop {
      height: 20px;
      line-height: 2rem;
      .title {
        display: none;
      }
      .s-cash.wrapper{
        width: 46.5%;
        .s-cash-txt {
          display: inline-block;
          color: ${theme.color.gray};
          font-size: ${theme.font.small};
          padding-left: 4px;
        }
      }
    }
    /* 승부사 알림 팝업 */
    #pwSubscribePopup {
      .pw-subscription-popup-inner {
        max-width: 500px;
      }
    }
    /* 가이드 */
    .character-popup .guide-character {
      right: 0;
    }

    .voc-page {
      .full-popup-content + div {

      }
    }

    .am-modal.event {
      /* 초기 팝업 */
      .notice-modal-wrap {
        width: 80%;
        max-width: ${PCModalMaxWidth};
      }
    }
    /* floating message button */
    .am-modal-wrap .am-modal-body {
      .message-wrapper.pc {
        &::before {
          width: ${PCModalMaxWidth};
          height: 300px;
        }
      }
    }
    // PC버전에서 페이지 형태의 팝업
    .pc-page-popup {
      position: absolute;
      top: 61px;
      left: 255px;
      right: initial;
      bottom: initial;
      transform: translate(0, 0);
      width: 549px;
      height: calc(100vh - 60px);
      *::-webkit-scrollbar {
        display: block;
      }
      .am-modal.full-modal {
        top: 0;
        left: 0;
        transform: translate(0, 0);
        max-width: 100% !important;
        max-height: 100%;
        .match-list {
          .am-list {
            min-height: calc(100vh - 197px);
          }
        }
      }
      ${mqMinPCPopup[0]} {
        left: 50%;
        transform: translate(-103.3%,0);
      }
    }
  }
`

message.config({
  duration: MESSAGE_SECOND_DURATION,
  maxCount: 1,
  top: 50,
})

function App() {
  return (
    <ThemeProvider theme={DarkTheme}>
      <Provider>
        <Global styles={GlobalStyle}/>
        <IntlWrapper>
          <Router history={history}>
            <HashRouter>
              <Switch>
                <Route path="/voc/" component={VocLoader}/>
                <Route exact path="/authkey_login/:authKey/:clubId" component={AuthKeyLogin}/>
                <Route exact path="/authkey_login/:authKey" component={AuthKeyLogin}/>
                <Route path="/login" component={Login}/>
                <Route path="/" component={PrimaryLayout}/>
                <Route render={() => <Error/>}/>
              </Switch>
            </HashRouter>
          </Router>
        </IntlWrapper>
      </Provider>
    </ThemeProvider>
  )
}

export default App
